import { useMemo, useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { useUserUsageQuery } from "@/v2-backoffice-client/user/usage/user.usage.query"
import {
  APP_CODE_LABELS,
  APP_MIX_TOOLTIP,
  formatAppArea,
  formatAppCode,
  GEO_AREAS_TOOLTIP,
  getScoreLevel,
  SCORE_LEVEL_LABELS,
  USAGE_REPORT_NOTES,
} from "@/v2-backoffice-client/usage/usage.constants"
import UsageGeoAreas from "@/v2-backoffice-client/usage/UsageGeoAreas"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageScoreMeter from "@/v2-backoffice-client/usage/UsageScoreMeter"
import UsageHistoryStrip from "@/v2-backoffice-client/usage/UsageHistoryStrip"
import UsageActivityChart from "@/v2-backoffice-client/usage/UsageActivityChart"
import UsageAppMix from "@/v2-backoffice-client/usage/UsageAppMix"
import UsageAppFilterChips from "@/v2-backoffice-client/usage/UsageAppFilterChips"
import UsageProductChips from "@/v2-backoffice-client/usage/UsageProductChips"
import UsageReportBarList from "@/v2-backoffice-client/usage/report/UsageReportBarList"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageReportNotes from "@/v2-backoffice-client/usage/report/UsageReportNotes"
import UsageReportProse from "@/v2-backoffice-client/usage/report/UsageReportProse"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import UsageMcpReport from "@/v2-backoffice-client/usage/mcp/UsageMcpReport"
import { useUsageMcpUserQuery } from "@/v2-backoffice-client/usage/mcp/usage.mcpUser.query"
import { MCP_HISTORY_DAYS } from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import { hasMcpActivity } from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import UsageLicenseDeniedSummary from "@/v2-backoffice-client/usage/denied/UsageLicenseDeniedSummary"
import {
  deriveAreaBreakdown,
  deriveDailyTotals,
  filterAreaDaysByApp,
  filterConsoleAreaDays,
  getAreaDayApps,
  padDailyTotals,
} from "@/v2-backoffice-client/usage/usage.utils"
import { activeApps } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import { list } from "@/v2-backoffice-client/usage/usage.text"
import { deriveUserStory } from "@/v2-backoffice-client/user/usage/user.usage.story"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"

const HISTORY_DAYS = 90
const USER_USAGE_TABS_ID = "user-usage"

export type UserUsageProps = {
  userId: number
  /** The person's name as the sheet shows it, for titles and prose. */
  subject: string
}

// Usage tab of the user panel: one overview, then one page per product.
// The Overview carries the person's profile (engagement, activity, days,
// breadth), the story in a paragraph, the product mix and what they asked
// for and do not have; Fusion, Placepoint MCP and Dataset MCP go deep on
// each product. The profile is computed daily in Databricks from console
// tracking events and synced into the app DB (acc.UserUsageProfiles /
// acc.UserUsageAreasDaily). Observations only — CRM workflow stays in
// HubSpot.
function UserUsage(props: UserUsageProps) {
  const { userId, subject } = props

  const { data, loading, error } = useUserUsageQuery({
    variables: { userId, days: HISTORY_DAYS },
  })
  const datasetQuery = useUsageMcpUserQuery({
    variables: { userId, days: MCP_HISTORY_DAYS, appCode: "mcp-dataset" },
  })

  const profile = data?.getUsageProfileByUser
  const areaDays = useMemo(
    () => data?.getUsageAreasDailyByUser ?? [],
    [data?.getUsageAreasDailyByUser],
  )
  const hasDataset = hasMcpActivity(
    datasetQuery.data?.getUsageMcpByUser.Days ?? [],
  )

  // The Fusion tab is the consoles' alone: a person who only works through
  // an MCP server must not read as a Fusion user there.
  const consoleAreaDays = useMemo(
    () => filterConsoleAreaDays(areaDays),
    [areaDays],
  )
  // App filter for the Fusion activity sections; null = every console.
  const [appFilter, setAppFilter] = useState<string | null>(null)
  const apps = useMemo(() => getAreaDayApps(consoleAreaDays), [consoleAreaDays])
  const filteredAreaDays = useMemo(
    () => filterAreaDaysByApp(consoleAreaDays, appFilter),
    [consoleAreaDays, appFilter],
  )
  // Zero-filled day-by-day history for the strip (quiet days must show).
  const history = useMemo(
    () => padDailyTotals(deriveDailyTotals(filteredAreaDays), HISTORY_DAYS),
    [filteredAreaDays],
  )
  // The same days across every product, for the Overview.
  const allHistory = useMemo(
    () => padDailyTotals(deriveDailyTotals(areaDays), HISTORY_DAYS),
    [areaDays],
  )
  const allActiveDays = allHistory.filter((entry) => entry.value > 0).length
  // Use cases with the 28-day-vs-previous trend per area.
  const areaBreakdown = useMemo(
    () => deriveAreaBreakdown(filteredAreaDays),
    [filteredAreaDays],
  )
  const appMix = useMemo(
    () =>
      (profile?.AppMix ?? []).map((entry) => ({
        app: entry.App,
        weighted28d: entry.Weighted28d,
        events28d: entry.Events28d,
        trendPct: entry.TrendPct,
        lastActiveAt: entry.LastActiveAt,
      })),
    [profile?.AppMix],
  )
  const story = useMemo(
    () => (profile ? deriveUserStory({ profile, appMix, subject }) : []),
    [profile, appMix, subject],
  )
  const productsInUse = activeApps(appMix)
  const otherProducts = productsInUse.filter((app) => !apps.includes(app))

  if (loading && !profile) {
    return <p className="p-4 text-sm text-muted-foreground">Loading…</p>
  }

  if (!profile) {
    return (
      <div className="flex flex-col gap-3 p-1">
        <ErrorMessage error={error} />
        <p className="usage-report-empty">
          No tracked usage yet. Profiles are computed every night from Fusion
          and MCP activity; a person with no tracked events (or only activity
          from before December 2025) has none.
        </p>
      </div>
    )
  }

  const trendPct = profile.WeightedTrendPct ?? profile.TrendPct
  const scoreLevel = getScoreLevel(profile.EngagementScore)
  const weightedActivity28d =
    profile.WeightedEvents28d != null
      ? Math.round(profile.WeightedEvents28d)
      : profile.Events28d

  return (
    <Tabs
      id={USER_USAGE_TABS_ID}
      defaultValue="overview"
      className="flex flex-col gap-6"
    >
      <Tabs.List className="self-start">
        <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
        <Tabs.Trigger value="fusion">Fusion</Tabs.Trigger>
        <Tabs.Trigger value="placepoint-mcp">
          {APP_CODE_LABELS["mcp-placepoint"]}
        </Tabs.Trigger>
        {hasDataset && (
          <Tabs.Trigger value="dataset-mcp">
            {APP_CODE_LABELS["mcp-dataset"]}
          </Tabs.Trigger>
        )}
      </Tabs.List>

      <Tabs.Content value="overview">
        <div className="usage-report">
          <UsageReportSection
            eyebrow="Overview"
            title={`How ${subject} uses Placepoint`}
            lead={
              <>
                Engagement, activity and breadth across every product, computed
                every night; last active{" "}
                <RelativeDate date={profile.LastActiveAt} />. The tabs next to
                this one go into each product.
              </>
            }
            aside={<UsageProductChips apps={productsInUse} />}
          >
            <ErrorMessage error={error} />

            <div className={usageKpiGridClass(4)}>
              <UsageReportKpi
                label="Engagement"
                value={
                  <>
                    {profile.EngagementScore ?? "—"}
                    <span className="truncate text-sm font-normal text-muted-foreground">
                      {SCORE_LEVEL_LABELS[scoreLevel]}
                    </span>
                  </>
                }
                sub={[
                  "out of 100: how much, how broadly, how recently, how steadily",
                  profile.EngagementScore != null ? (
                    <UsageScoreMeter
                      key="meter"
                      score={profile.EngagementScore}
                    />
                  ) : (
                    "no score yet"
                  ),
                ]}
              />
              <UsageReportKpi
                label="Activity"
                value={
                  <>
                    {formatNumber(weightedActivity28d)}
                    <UsageTrendBadge trendPct={trendPct} />
                  </>
                }
                sub="weighted actions in the last 28 days, against the four weeks before"
              />
              <UsageReportKpi
                label="Active days"
                value={
                  <>
                    {profile.ActiveDays28d}
                    <span className="text-sm font-normal text-muted-foreground">
                      of 28
                    </span>
                  </>
                }
                sub={
                  <>
                    last active <RelativeDate date={profile.LastActiveAt} />
                  </>
                }
              />
              <UsageReportKpi
                label="Breadth"
                value={
                  <>
                    {profile.DistinctAreas28d ?? 0}
                    <span className="text-sm font-normal text-muted-foreground">
                      use cases
                    </span>
                  </>
                }
                sub={
                  profile.TopAreas28d[0]
                    ? `mostly ${formatAppArea(profile.TopAreas28d[0].Area)}`
                    : "no use-case activity yet"
                }
              />
            </div>

            <div className="usage-report-grid usage-report-grid--two">
              <UsageReportProse title="In short" paragraphs={story} />
              <UsageReportCard
                title="Which products carry the work"
                caption={APP_MIX_TOOLTIP}
              >
                <UsageAppMix entries={appMix} />
              </UsageReportCard>
              <UsageReportCard
                title={`Day by day, last ${HISTORY_DAYS} days`}
                caption={`Every product together: ${formatNumber(allActiveDays)} ${allActiveDays === 1 ? "day" : "days"} with activity in the last ${HISTORY_DAYS}. Darker is a busier day.`}
                wide
              >
                <UsageHistoryStrip entries={allHistory} />
              </UsageReportCard>
              {profile.AutomationSuspect && (
                <UsageReportProse
                  title="Looks automated"
                  tone="warning"
                  wide
                  paragraphs={[
                    `${profile.SuspectHours28d ?? 0} hour${(profile.SuspectHours28d ?? 0) === 1 ? "" : "s"} in the last 28 days showed machine-like use: sustained rates beyond what a person clicks (peak ${formatNumber(profile.PeakEventsPerHour28d)} events an hour${
                      profile.MedianPeakGapSeconds != null
                        ? `, a steady ${profile.MedianPeakGapSeconds.toFixed(0)} s between actions`
                        : ""
                    }). Worth an API-license conversation.`,
                  ]}
                />
              )}
            </div>
          </UsageReportSection>

          <UsageLicenseDeniedSummary userId={userId} subject={subject} />
        </div>
      </Tabs.Content>

      <Tabs.Content value="fusion">
        <div className="usage-report">
          {consoleAreaDays.length === 0 ? (
            <UsageReportSection
              eyebrow="Fusion"
              title={`What ${subject} does in Fusion`}
              lead="Console work day by day, the use cases it falls into and where in the country it happens."
            >
              <p className="usage-report-empty">
                {subject} has not worked in Fusion in the last {HISTORY_DAYS}{" "}
                days.
                {otherProducts.length > 0
                  ? ` The activity is in ${list(otherProducts.map(formatAppCode))}: the ${otherProducts.length === 1 ? "tab" : "tabs"} next to this one ${otherProducts.length === 1 ? "has" : "have"} it.`
                  : ""}
              </p>
            </UsageReportSection>
          ) : (
            <UsageReportSection
              eyebrow="Fusion"
              title={`What ${subject} does in Fusion`}
              lead="Console work day by day, the use cases it falls into and where in the country it happens. Activity is weighted by action — exports, reports and saves count more than clicks."
              aside={<UsageProductChips apps={apps} />}
            >
              <div className="usage-report-grid usage-report-grid--two">
                {/* The chart brings its own header row (title, legend, range
                  switch): no card. The strip above it shows the same days at
                  a glance; the chips pick the product. */}
                <div className="col-span-full flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      {history.filter((entry) => entry.value > 0).length} days
                      with activity in the last {HISTORY_DAYS}
                    </span>
                    <UsageAppFilterChips
                      apps={apps}
                      value={appFilter}
                      onChange={setAppFilter}
                    />
                  </div>
                  <UsageHistoryStrip entries={history} />
                  <UsageActivityChart areaDays={filteredAreaDays} />
                </div>

                <UsageReportCard
                  title="What they use"
                  caption="Weighted activity per use case in the last 28 days, with the trend on the four weeks before."
                >
                  <UsageReportBarList
                    rows={areaBreakdown.map((area) => ({
                      key: area.area,
                      label: formatAppArea(area.area),
                      value: Math.round(area.current),
                      extra: (
                        <UsageTrendBadge
                          trendPct={area.trendPct}
                          className="w-16 justify-center"
                        />
                      ),
                    }))}
                    labelWidth="w-40"
                    emptyText="No use-case activity in the last 28 days."
                  />
                </UsageReportCard>

                <UsageReportCard
                  title="Where they work"
                  info={GEO_AREAS_TOOLTIP}
                  caption={
                    (profile.GeoEvents28d ?? 0) > 0
                      ? `Municipalities where ${formatNumber(profile.GeoEvents28d ?? 0)} located events of the last 28 days concentrate.`
                      : "Municipalities where their located work concentrates."
                  }
                >
                  {profile.TopGeoAreas28d.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No located work yet. Municipalities appear once tracked
                      work carries a property or a map position.
                    </p>
                  ) : (
                    <UsageGeoAreas areas={profile.TopGeoAreas28d} />
                  )}
                </UsageReportCard>

                <UsageReportCard wide>
                  <UsageReportNotes
                    notes={[
                      ...USAGE_REPORT_NOTES,
                      {
                        term: "Computed",
                        text: `Every night in the usage pipeline; last run ${formatDateString(profile.ComputedAt)}.`,
                      },
                    ]}
                  />
                </UsageReportCard>
              </div>
            </UsageReportSection>
          )}
        </div>
      </Tabs.Content>

      <Tabs.Content value="placepoint-mcp">
        <div className="usage-report">
          <UsageMcpReport
            userId={userId}
            appCode="mcp-placepoint"
            productName={APP_CODE_LABELS["mcp-placepoint"]}
            subject={subject}
          />
        </div>
      </Tabs.Content>

      {hasDataset && (
        <Tabs.Content value="dataset-mcp">
          <div className="usage-report">
            <UsageMcpReport
              userId={userId}
              appCode="mcp-dataset"
              productName={APP_CODE_LABELS["mcp-dataset"]}
              subject={subject}
            />
          </div>
        </Tabs.Content>
      )}
    </Tabs>
  )
}

export default UserUsage
